import { TABLE_MODES } from '../../constants/agGridStyles';

export const SKELETON_KINDS = {
  CHART: 'chart',
  DETAIL: 'detail',
  FILTER: 'filter',
  SPLIT_CHART_TABLE: 'splitChartTable',
  TABLE: 'table',
};

export const CHART_SKELETON_VARIANTS = {
  ACCELERATION: 'acceleration',
  BAR: 'bar',
  BOXPLOT: 'boxplot',
  LINE: 'line',
  SCATTER: 'scatter',
  STACKED_BAR: 'stackedBar',
  TREEMAP: 'treemap',
  WATERFALL: 'waterfall',
};

export const SKELETON_DENSITIES = {
  COMPACT: 'compact',
  STANDARD: 'standard',
  DENSE: 'dense',
  FULL_PAGE: 'fullPage',
};

export const SKELETON_HEADER_VARIANTS = {
  ACTIONS: 'actions',
  PLAIN: 'plain',
};

export const CHART_SKELETON_PRESETS = {
  trendLine: {
    kind: SKELETON_KINDS.CHART,
    header: true,
    variant: CHART_SKELETON_VARIANTS.LINE,
    height: '55vh',
    width: '90%',
    minHeight: 350,
    seriesCount: 3,
  },
  barTrend: {
    kind: SKELETON_KINDS.CHART,
    header: true,
    variant: CHART_SKELETON_VARIANTS.BAR,
    height: '35vh',
    width: '80%',
    minHeight: 250,
  },
  scatter: {
    kind: SKELETON_KINDS.CHART,
    header: true,
    variant: CHART_SKELETON_VARIANTS.SCATTER,
    height: '65vh',
    width: '90%',
    minHeight: 350,
  },
  acceleration: {
    kind: SKELETON_KINDS.CHART,
    header: true,
    variant: CHART_SKELETON_VARIANTS.ACCELERATION,
    height: '70vh',
    width: '90%',
    minHeight: 350,
  },
  distribution: {
    kind: SKELETON_KINDS.CHART,
    header: true,
    variant: CHART_SKELETON_VARIANTS.BOXPLOT,
    height: '75vh',
    width: '90%',
    minHeight: 400,
  },
  roleMix: {
    kind: SKELETON_KINDS.CHART,
    header: true,
    variant: CHART_SKELETON_VARIANTS.STACKED_BAR,
    height: '70vh',
    width: '100%',
    minHeight: 500,
  },
};

export const TABLE_SKELETON_PRESETS = {
  compactSignal: {
    kind: SKELETON_KINDS.TABLE,
    header: true,
    density: SKELETON_DENSITIES.COMPACT,
    rows: 4,
    columns: 6,
    height: 220,
    tableWidth: '80%',
  },
  signal: {
    kind: SKELETON_KINDS.TABLE,
    header: true,
    density: SKELETON_DENSITIES.STANDARD,
    rows: 10,
    columns: 6,
    height: 300,
    tableWidth: '80%',
  },
  leadership: {
    kind: SKELETON_KINDS.TABLE,
    header: true,
    density: SKELETON_DENSITIES.DENSE,
    rows: 30,
    columns: 4,
    height: '70vh',
    tableWidth: 920,
  },
  compsheet: {
    kind: SKELETON_KINDS.TABLE,
    header: true,
    density: SKELETON_DENSITIES.DENSE,
    rows: 30,
    columns: 10,
    height: '75vh',
    tableWidth: '90%',
    mode: TABLE_MODES.COMPSHEET,
    showPinnedRows: true,
  },
};

export const DETAIL_SKELETON_PRESETS = {
  filingChange: {
    kind: SKELETON_KINDS.DETAIL,
    height: 360,
    width: '100%',
  },
};

const SKELETON_REGISTRIES = {
  chart: CHART_SKELETON_PRESETS,
  detail: DETAIL_SKELETON_PRESETS,
  table: TABLE_SKELETON_PRESETS,
};

const CHART_VARIANT_VALUES = new Set(Object.values(CHART_SKELETON_VARIANTS));
const SKELETON_HEADER_VARIANT_VALUES = new Set(Object.values(SKELETON_HEADER_VARIANTS));
const SKELETON_KIND_VALUES = new Set(Object.values(SKELETON_KINDS));

const isPlainObject = (value) => (
  value != null
  && typeof value === 'object'
  && !Array.isArray(value)
);

const isFiniteNumber = (value) => Number.isFinite(Number(value));

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const countVisibleLeafColumns = (columnDefs = []) => {
  if (!Array.isArray(columnDefs)) return 0;

  return columnDefs.reduce((sum, colDef) => {
    if (!colDef || colDef.hide) return sum;
    if (Array.isArray(colDef.children) && colDef.children.length > 0) {
      return sum + countVisibleLeafColumns(colDef.children);
    }
    return sum + 1;
  }, 0);
};

const countChartSeries = (chartData) => {
  if (!Array.isArray(chartData)) return 1;
  if (chartData.length === 0) return 1;

  const first = chartData[0];
  if (Array.isArray(first?.data)) return chartData.length;
  if (isPlainObject(first)) {
    return Object.keys(first).filter((key) => (
      key !== 'id'
      && key !== 'label'
      && key !== 'date'
      && key !== 'week'
      && typeof first[key] === 'number'
    )).length || 1;
  }

  return 1;
};

const countChartPoints = (chartData) => {
  if (!Array.isArray(chartData)) return 0;
  if (chartData.length === 0) return 0;

  const first = chartData[0];
  if (Array.isArray(first?.data)) {
    return Math.max(...chartData.map((series) => Array.isArray(series.data) ? series.data.length : 0));
  }
  return chartData.length;
};

const inferChartVariant = (visualizationKind) => {
  switch (visualizationKind) {
    case 'trendLine':
      return CHART_SKELETON_VARIANTS.LINE;
    case 'scatter':
    case 'quadrant':
      return CHART_SKELETON_VARIANTS.SCATTER;
    case 'roleMix':
    case 'stackedBar':
      return CHART_SKELETON_VARIANTS.STACKED_BAR;
    case 'bridge':
      return CHART_SKELETON_VARIANTS.WATERFALL;
    case 'heatmap':
      return CHART_SKELETON_VARIANTS.TREEMAP;
    case 'groupedBar':
    default:
      return CHART_SKELETON_VARIANTS.BAR;
  }
};

const normalizeSkeletonHeader = (header) => {
  if (!header) return null;

  if (header === true) {
    return {
      variant: SKELETON_HEADER_VARIANTS.ACTIONS,
      actions: true,
    };
  }

  if (typeof header === 'string') {
    return {
      variant: header,
      actions: header !== SKELETON_HEADER_VARIANTS.PLAIN,
    };
  }

  if (isPlainObject(header)) {
    const variant = header.variant || (header.actions === false
      ? SKELETON_HEADER_VARIANTS.PLAIN
      : SKELETON_HEADER_VARIANTS.ACTIONS);

    return {
      ...header,
      variant,
      actions: header.actions !== false && variant !== SKELETON_HEADER_VARIANTS.PLAIN,
    };
  }

  return null;
};

const inferSkeletonHeaderWidth = (kind, props = {}) => {
  if (kind === SKELETON_KINDS.TABLE) {
    return props.tableWidth || props.width || '100%';
  }

  if (kind === SKELETON_KINDS.CHART) {
    return props.width || '100%';
  }

  return props.width || props.tableWidth || '100%';
};

const resolveSkeletonHeader = (header, kind, props = {}) => {
  const normalizedHeader = normalizeSkeletonHeader(header);
  if (!normalizedHeader) return null;

  return {
    ...normalizedHeader,
    width: normalizedHeader.width || inferSkeletonHeaderWidth(kind, props),
    maxWidth: normalizedHeader.maxWidth || props.maxWidth || '100%',
  };
};

export const getSkeletonPreset = (kind, preset) => {
  if (!kind || !preset) return null;
  return SKELETON_REGISTRIES[kind]?.[preset] || null;
};

export const normalizeSkeletonSpec = (spec) => {
  if (!isPlainObject(spec)) return null;

  const presetSpec = getSkeletonPreset(spec.kind, spec.preset);
  const merged = {
    ...(presetSpec || {}),
    ...spec,
  };
  const kind = merged.kind || SKELETON_KINDS.CHART;

  if (kind === SKELETON_KINDS.SPLIT_CHART_TABLE) {
    return {
      ...merged,
      kind,
      chart: normalizeSkeletonSpec({
        kind: SKELETON_KINDS.CHART,
        ...(merged.chart || {}),
      }),
      table: normalizeSkeletonSpec({
        kind: SKELETON_KINDS.TABLE,
        ...(merged.table || {}),
      }),
    };
  }

  if (kind === SKELETON_KINDS.CHART) {
    return {
      ...merged,
      kind,
      variant: merged.variant || CHART_SKELETON_VARIANTS.BAR,
      density: merged.density || SKELETON_DENSITIES.STANDARD,
    };
  }

  if (kind === SKELETON_KINDS.TABLE) {
    return {
      ...merged,
      kind,
      density: merged.density || SKELETON_DENSITIES.STANDARD,
      rows: isFiniteNumber(merged.rows) ? Number(merged.rows) : 8,
      columns: isFiniteNumber(merged.columns) ? Number(merged.columns) : 6,
    };
  }

  if (kind === SKELETON_KINDS.DETAIL) {
    return {
      ...merged,
      kind,
      density: merged.density || SKELETON_DENSITIES.STANDARD,
    };
  }

  if (kind === SKELETON_KINDS.FILTER) {
    return {
      ...merged,
      kind,
      filterCount: isFiniteNumber(merged.filterCount) ? Number(merged.filterCount) : 3,
    };
  }

  return merged;
};

export const validateSkeletonSpec = (spec) => {
  const normalized = normalizeSkeletonSpec(spec);
  const errors = [];

  if (!normalized) {
    return {
      valid: false,
      errors: ['skeleton spec must be an object'],
      spec: normalized,
    };
  }

  if (!SKELETON_KIND_VALUES.has(normalized.kind)) {
    errors.push(`unknown skeleton kind "${normalized.kind}"`);
  }

  if (normalized.kind === SKELETON_KINDS.CHART && !CHART_VARIANT_VALUES.has(normalized.variant)) {
    errors.push(`unknown chart skeleton variant "${normalized.variant}"`);
  }

  const normalizedHeader = normalizeSkeletonHeader(normalized.header);
  if (normalizedHeader && !SKELETON_HEADER_VARIANT_VALUES.has(normalizedHeader.variant)) {
    errors.push(`unknown skeleton header variant "${normalizedHeader.variant}"`);
  }

  if (normalized.kind === SKELETON_KINDS.TABLE) {
    if (!Number.isFinite(normalized.rows) || normalized.rows < 0) {
      errors.push('table skeleton rows must be a non-negative number');
    }
    if (!Number.isFinite(normalized.columns) || normalized.columns <= 0) {
      errors.push('table skeleton columns must be a positive number');
    }
  }

  if (normalized.kind === SKELETON_KINDS.SPLIT_CHART_TABLE) {
    const chartResult = validateSkeletonSpec(normalized.chart);
    const tableResult = validateSkeletonSpec(normalized.table);
    errors.push(
      ...chartResult.errors.map((error) => `chart.${error}`),
      ...tableResult.errors.map((error) => `table.${error}`)
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    spec: normalized,
  };
};

export const resolveSkeletonSpec = ({
  skeletonSpec,
  skeletonType = SKELETON_KINDS.CHART,
  skeletonProps = {},
  minHeight,
} = {}) => {
  const normalized = normalizeSkeletonSpec(skeletonSpec);

  if (!normalized) {
    return {
      kind: skeletonType,
      props: skeletonProps || {},
      minHeight,
    };
  }

  if (normalized.kind === SKELETON_KINDS.SPLIT_CHART_TABLE) {
    return {
      kind: normalized.kind,
      props: {
        chart: resolveSkeletonSpec({ skeletonSpec: normalized.chart }).props,
        table: resolveSkeletonSpec({ skeletonSpec: normalized.table }).props,
      },
      minHeight: normalized.minHeight ?? minHeight,
    };
  }

  const {
    header,
    kind,
    minHeight: specMinHeight,
    preset,
    density,
    pointCount,
    ...props
  } = normalized;

  return {
    kind,
    props,
    minHeight: specMinHeight ?? minHeight,
    header: resolveSkeletonHeader(header, kind, props),
    meta: {
      density,
      pointCount,
      preset,
    },
  };
};

export const calibrateSkeletonSpec = ({
  kind,
  rows,
  columnDefs,
  chartData,
  visualizationKind,
  layout = {},
  density = SKELETON_DENSITIES.STANDARD,
} = {}) => {
  if (kind === SKELETON_KINDS.TABLE) {
    const rowCount = Array.isArray(rows) ? rows.length : Number(rows) || 0;
    const columnCount = countVisibleLeafColumns(columnDefs);

    return normalizeSkeletonSpec({
      kind: SKELETON_KINDS.TABLE,
      density,
      rows: clamp(rowCount || 8, 4, 30),
      columns: clamp(columnCount || 6, 1, 12),
      height: layout.height,
      tableWidth: layout.width || layout.tableWidth,
      maxWidth: layout.maxWidth,
      mode: layout.mode,
      showPinnedRows: Boolean(layout.showPinnedRows),
    });
  }

  const pointCount = countChartPoints(chartData);
  const seriesCount = countChartSeries(chartData);

  return normalizeSkeletonSpec({
    kind: SKELETON_KINDS.CHART,
    density,
    variant: inferChartVariant(visualizationKind),
    height: layout.height,
    width: layout.width,
    maxWidth: layout.maxWidth,
    minHeight: layout.minHeight,
    pointCount,
    seriesCount: clamp(seriesCount, 1, 5),
  });
};
